import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    const reference = searchParams.get('reference') || searchParams.get('trxref');

    if (reference) {
      // Verify payment
      fetch(`${API}/bookings/payment/verify/${reference}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status === 'success') {
            setStatus('success');
            setBookingDetails(data.data);
          } else {
            setStatus('failed');
          }
        })
        .catch((error) => {
          console.error('Payment verification error:', error);
          setStatus('failed');
        });
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  return (
    <div data-testid="payment-callback-page" className="min-h-screen bg-[#FAF8F5] pt-24">
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto text-center">
          {status === 'verifying' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              data-testid="payment-verifying"
            >
              <Loader className="text-[#C9A84C] mx-auto mb-6 animate-spin" size={64} />
              <h1 className="font-serif text-3xl sm:text-4xl tracking-tight font-light text-[#1A1A1A] mb-4">
                Verifying Your Payment...
              </h1>
              <p className="text-base leading-relaxed text-[#3A3A3A]">
                Please wait while we confirm your booking.
              </p>
            </motion.div>
          )}

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              data-testid="payment-success"
            >
              <CheckCircle className="text-green-600 mx-auto mb-6" size={64} />
              <h1 className="font-serif text-3xl sm:text-4xl tracking-tight font-light text-[#1A1A1A] mb-4">
                Booking Confirmed!
              </h1>
              <p className="text-base leading-relaxed text-[#3A3A3A] mb-8">
                Thank you for choosing De Azraa Luxury Apartments. Your payment was successful and your booking has been confirmed.
              </p>
              
              <div className="bg-white border border-[#E5E5E5] p-8 text-left mb-8">
                <h2 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A] mb-6">
                  Booking Details
                </h2>
                {bookingDetails && (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#3A3A3A]">Reference:</span>
                      <span className="text-[#1A1A1A] font-medium" data-testid="payment-reference">
                        {bookingDetails.reference}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#3A3A3A]">Amount Paid:</span>
                      <span className="text-[#1A1A1A] font-medium" data-testid="payment-amount">
                        ₦{(bookingDetails.amount / 100).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-sm text-[#3A3A3A] mb-8">
                A confirmation email has been sent to your email address. We look forward to welcoming you!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/')}
                  data-testid="payment-home-btn"
                  className="bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase"
                >
                  Back to Home
                </button>
                <a
                  href="https://wa.me/2348066722221"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="payment-whatsapp-btn"
                  className="bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase text-center"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>
          )}

          {status === 'failed' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              data-testid="payment-failed"
            >
              <XCircle className="text-red-600 mx-auto mb-6" size={64} />
              <h1 className="font-serif text-3xl sm:text-4xl tracking-tight font-light text-[#1A1A1A] mb-4">
                Payment Verification Failed
              </h1>
              <p className="text-base leading-relaxed text-[#3A3A3A] mb-8">
                We couldn't verify your payment. This could be due to a cancelled transaction or a network issue.
              </p>
              
              <div className="bg-red-50 border border-red-200 p-6 mb-8">
                <p className="text-sm text-red-800">
                  If your account was debited, please contact us immediately with your transaction reference.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/book')}
                  data-testid="payment-retry-btn"
                  className="bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase"
                >
                  Try Again
                </button>
                <a
                  href="tel:+2348066722221"
                  data-testid="payment-call-btn"
                  className="border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase text-center"
                >
                  Call Us: +234 806 672 2221
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PaymentCallback;